import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { ToastCloseReasons, ToastRef } from "@merri/common";
import { BehaviorSubject, catchError, Observable, of, retryWhen, Subject, switchMap, take, takeUntil, timer } from "rxjs";
import { AlertsService, AlertTypes, AppConfigService, LoggingService, PartnerUserService, rxjsRetryStrategy } from "src/app/core";
import { VenueAdminReportData } from "../models/venue-admin-report-data";
import { GetVenueAdminReportsResponse, VenueAdminResponses } from "../responses/venue-admin-responses";
import { VenueAdminService } from "./venue-admin.service";
import { HelperMethods } from "src/app/shared";

const Workflow: string = "VenueAdminReports";
const WorkflowSource: string = "VenueAdminReportService";

const ALERT_KEYS: AlertTypes[] = [AlertTypes.VenueInventoryOverbookedReport];
const POLLING_INTERVAL: number = 900000;

@Injectable({
  providedIn: "root",
})
export class VenueAdminReportsService implements OnDestroy {
  private _destroyed$: Subject<void> = new Subject<void>();

  private _venueAdminReportData$: BehaviorSubject<VenueAdminReportData> = new BehaviorSubject<VenueAdminReportData>({
    overbookedReportVenueIds: [],
  });
  venueAdminReportData$: Observable<VenueAdminReportData> = this._venueAdminReportData$.asObservable();

  constructor(
    private _http: HttpClient,
    private _appConfigService: AppConfigService,
    private _partnerUserService: PartnerUserService,
    private _alertsService: AlertsService,
    private _loggingService: LoggingService,
    private _router: Router,
    private _venueAdminService: VenueAdminService,
  ) {
    this._alertsService.alertSettings$
      .pipe(
        switchMap(alertSettings => {
          if (!!alertSettings && this.alertActive()) {
            return timer(0, POLLING_INTERVAL).pipe(
              switchMap(() =>
                this._http
                  .get<GetVenueAdminReportsResponse>(this._appConfigService.getProtectedConfig().venueAdminInventoryReportsUrl)
                  .pipe(
                    retryWhen(rxjsRetryStrategy({ maxRetry: 3, scaleDelay: false })),
                    catchError(error => {
                      this._loggingService.logWorkflowWarning(
                        "Error fetching venue admin report data for alerts",
                        "VenueAdminReportService.ctor",
                        Workflow,
                        "FetchReportDataForAlerts",
                        WorkflowSource,
                        JSON.stringify(HelperMethods.getResponseErrors(error)),
                        error,
                        {},
                      );
                      return of(null);
                    }),
                  ),
              ),
            );
          }

          return of(null);
        }),
        takeUntil(this._destroyed$),
      )
      .subscribe({
        next: response => {
          if (!response) {
            return;
          }

          this._venueAdminReportData$.next(VenueAdminResponses.getVenueAdminReportDataFromResponse(response));
        },
      });

    this._venueAdminReportData$.pipe(takeUntil(this._destroyed$)).subscribe({
      next: data => {
        if (!this.alertActive()) {
          return;
        }
        let toastRef: ToastRef;

        if (data.overbookedReportVenueIds.length) {
          toastRef = this._alertsService.handleAlert(AlertTypes.VenueInventoryOverbookedReport);

          toastRef?.afterDismiss$.pipe(take(1)).subscribe({
            next: reason => {
              if (reason === ToastCloseReasons.Click) {
                this._venueAdminService.setActiveVenueById(data.overbookedReportVenueIds[0]);
                this._router.navigateByUrl("/partner-portal/venue-settings/inventory");
              }
            },
          });
        }
      },
    });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  hasAlert(venueId: number): boolean {
    return this._venueAdminReportData$.value.overbookedReportVenueIds.includes(venueId);
  }

  setOverbookedReport(venueId: number, hasOverbookedReport: boolean): void {
    const venueAdminReportData = this._venueAdminReportData$.value;

    if (hasOverbookedReport && !venueAdminReportData.overbookedReportVenueIds.includes(venueId)) {
      venueAdminReportData.overbookedReportVenueIds.push(venueId);
    } else if (!hasOverbookedReport && venueAdminReportData.overbookedReportVenueIds.includes(venueId)) {
      venueAdminReportData.overbookedReportVenueIds.splice(venueAdminReportData.overbookedReportVenueIds.indexOf(venueId), 1);
    }

    this._venueAdminReportData$.next(venueAdminReportData);
  }

  alertActive(): boolean {
    return this._partnerUserService.currentPartnerHasVenues() && this._alertsService.hasAlertSettingsActive(...ALERT_KEYS);
  }
}
